import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import cn from 'classnames';
import { alignmentType } from '../../propTypes';
import theme, { expandAbsolutely } from '../../styles/theme';
import round from '../../helpers/round';
import { resolvePosition, resolveAlignment } from './container';

const ContentContainer = ({
  children, classes, className, edgeToEdge, largeContent,
  domRef,
}) => {
  const child = React.Children.only(children);
  const contentClassName = largeContent ? classes.largeContent : classes.content;
  return (
    <div
      ref={domRef}
      className={cn(className, classes.container, edgeToEdge && classes.edgeToEdge)}
    >
      {React.cloneElement(
        child,
        { className: cn(child.props.className, contentClassName, classes.content) },
      )}
    </div>
  );
};

ContentContainer.propTypes = {
  alignment: alignmentType, // eslint-disable-line react/no-unused-prop-types -- used in injectSheet()
  children: PropTypes.element.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  edgeToEdge: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps()
  position: alignmentType,
  // eslint-disable-next-line react/no-unused-prop-types -- used in injectSheet()
  topBiases: PropTypes.objectOf(PropTypes.number),
  largeContent: PropTypes.bool,
  domRef: PropTypes.func,
};

ContentContainer.defaultProps = {
  alignment: {},
  className: undefined,
  edgeToEdge: true,
  position: {},
  topBiases: {},
  largeContent: false,
  domRef: undefined,
};

const calculatePadding = (horz, topBias = 1, units = 'px') => {
  const padding = `${round(horz)}${units}`;
  return `${padding} ${padding} ${round(horz * topBias)}${units}`;
};

export default injectSheet({
  container: {
    ...expandAbsolutely,
    display: 'flex',
    alignItems: 'center',
    justifyContent: ({ position }) => resolvePosition(position.mobile),
    textAlign: ({ alignment }) => resolveAlignment(alignment.mobile),
    padding: ({ topBiases }) => calculatePadding(20, topBiases.sm),
    [theme.breakpoints.up('md')]: {
      padding: ({ topBiases }) => calculatePadding(40, topBiases.md),
      justifyContent: ({ position }) => resolvePosition(position.desktop),
      textAlign: ({ alignment }) => resolveAlignment(alignment.desktop),
    },
    [theme.breakpoints.up('lg')]: {
      padding: ({ topBiases }) => calculatePadding(60, topBiases.lg),
    },
  },
  edgeToEdgeContainer: {
    [theme.breakpoints.up('xxl')]: {
      padding: ({ topBiases }) => calculatePadding(
        100 * 60 / theme.breakpoints.width('xxl'),
        topBiases.lg,
        'vw',
      ),
    },
  },
  content: {
    flex: '0 1 50%',
    [theme.breakpoints.up('md')]: {
      flexBasis: '25%',
    },
  },
  largeContent: {
    flex: '0 1 100%',
    [theme.breakpoints.up('md')]: {
      flexBasis: '50%',
    },
  },
})(ContentContainer);
